import { StoreService } from "../../../shared/store/store/store.service";
import { Dashboard, Widget, WidgetType } from "../models/dashboard.model";
import { computed, inject, Injectable } from "@angular/core";
import { DashboardApiService } from "./dashboard-api.service";
import { TypeDashboard } from "../enums/dashboard-type.enum";
import { firstValueFrom } from "rxjs";
import * as uuid from "uuid";
import { Untrack } from "../../../shared/store/utils/store-utils.service";
import { DisplayService } from "../../home/services/display.service";
import { DashboardHelperService } from "./dashboard-helper.service";
import { WidgetHelperService } from "./widget-helper.service";
import { PassFacadeService } from "../../administration/services/pass-store/pass-facade.service";

export interface DashboardState {
  dashboard: Dashboard;
  edition: boolean;
  globalDashboard: boolean;
  displayWidgets: Widget[];
  squeezeWidgets: WidgetType[];
  loading: boolean;
  saving: boolean;
}

@Injectable({ providedIn: "root" })
export class DashboardFacadeService extends StoreService<DashboardState> {
  private api = inject(DashboardApiService);
  private displayService = inject(DisplayService);
  private passFacade = inject(PassFacadeService);
  private currentPass = this.passFacade.currentPass;

  constructor() {
    super({
      dashboard: null,
      edition: false,
      globalDashboard: false,
      displayWidgets: [],
      squeezeWidgets: [],
      loading: false,
      saving: false,
    });
  }

  static DESKTOP_COLUMN = 4;
  static MOBILE_COLUMN = 1;
  dashboardColumn = computed(() =>
    this.displayService.isMobile()
      ? DashboardFacadeService.MOBILE_COLUMN
      : DashboardFacadeService.DESKTOP_COLUMN,
  );
  displayWidgets = computed(() => {
    const widgets = this.displayService.isMobile()
      ? this._state.displayWidgets().map((w) => {
          return {
            ...w,
            y: w.mobileIndex,
            x: 0,
            w: 1,
          };
        })
      : this._state.displayWidgets();
    const widgetAccess = widgets.filter((w) =>
      WidgetHelperService.hasAccessWidget(w.type, this.currentPass()),
    );

    // Dé-duplication des widgets fusionnés : VENTES_TIERS et MARGE_TIERS
    // rendent désormais le même composant (vl-repartition-ca-tiers).
    // Si les deux sont présents, on ne garde que MARGE_TIERS.
    const hasMarge = widgetAccess.some((w) => w.type === WidgetType.MARGE_TIERS);
    const deduped = hasMarge
      ? widgetAccess.filter((w) => w.type !== WidgetType.VENTES_TIERS)
      : widgetAccess;

    return this.recomputeAfterSqueeze(
      deduped,
      this._state.squeezeWidgets(),
    );
  });

  panelWidget = computed(() => {
    const widgets = this.displayWidgets();
    const pass = this.currentPass();
    return WidgetHelperService.widgets
      .map((categorie) => ({
        ...categorie,
        modules: categorie.modules
          .filter((module) =>
            WidgetHelperService.hasAccessWidget(module.type, pass),
          )
          .map((module) => ({
            ...module,
            enabled: !!widgets.find((w) => w.type === module.type),
          })),
      }))
      .filter((cat) => cat.modules.length > 0);
  });

  @Untrack
  async loadDashboard(type: TypeDashboard) {
    this.patchState({ loading: true, displayWidgets: [] });
    let dashboard: Dashboard;
    try {
      if (this.getState().globalDashboard()) {
        dashboard = await firstValueFrom(this.api.loadDashboardGlobal(type));
      } else {
        dashboard = await firstValueFrom(this.api.loadDashboard(type));
      }
      dashboard = {
        ...dashboard,
        widgets: dashboard.widgets?.map((w) => ({
          ...w,
          id: w.id ?? uuid.v4(),
        })),
      };
      this.patchState({ dashboard, displayWidgets: [...dashboard.widgets] });
    } finally {
      this.patchState({ loading: false });
    }
  }

  @Untrack
  async saveDashboard(dashboarToSave: Dashboard) {
    this.patchState({ saving: true });
    let dashboard;
    try {
      if (this.getState().globalDashboard()) {
        dashboard = await firstValueFrom(
          this.api.saveGlobalDashboard(dashboarToSave),
        );
      } else {
        dashboard = await firstValueFrom(
          this.api.saveDashboard({
            ...dashboarToSave,
            id: dashboarToSave.userId ? dashboarToSave.id : null,
          }),
        );
      }
      this.patchState({ dashboard, displayWidgets: dashboard.widgets });
    } finally {
      this.patchState({ saving: false });
    }
  }

  @Untrack
  resetDisplayWidget() {
    this._state.displayWidgets.set(this.getState().dashboard()?.widgets ?? []);
  }

  @Untrack
  async updateDisplayWidgets(widgets: Widget[]) {
    if (this.displayService.isMobile()) {
      this._state.displayWidgets.set(
        widgets.map((w) => {
          const existing = this._state
            .displayWidgets()
            .find((cw) => cw.id === w.id);
          return {
            ...existing,
            mobileIndex: w.y,
          };
        }),
      );
    } else {
      this._state.displayWidgets.set(widgets);
    }
    await this.saveDashboard({
      ...this._state.dashboard(),
      widgets: [...this._state.displayWidgets()],
    });
  }

  @Untrack
  removeWidget(widget: Widget) {
    this._state.displayWidgets.set(
      this._state.displayWidgets().filter((w) => w.id !== widget.id),
    );
    this.saveDashboard({
      ...this._state.dashboard(),
      widgets: [...this._state.displayWidgets()],
    });
  }

  @Untrack
  async addWidget(widget: Widget) {
    this._state.displayWidgets.set([
      ...this._state.displayWidgets().filter((w) => w.id !== widget.id),
      {
        mobileIndex:
          Math.max(...this._state.displayWidgets().map((w) => w.mobileIndex)) +
          1,
        ...DashboardHelperService.findFirstEmptyCell(
          this._state.displayWidgets(),
          DashboardFacadeService.DESKTOP_COLUMN,
          widget.type,
        ),
        type: widget.type,
        subType: widget.subType,
        id: uuid.v4(),
        color: 1,
      },
    ]);
    await this.saveDashboard({
      ...this._state.dashboard(),
      widgets: [...this._state.displayWidgets()],
    });
  }

  @Untrack
  async updateWidget(widget: Widget) {
    const saved = await this.api.updateWidget(widget);
    this.patchState({
      displayWidgets: this._state
        .displayWidgets()
        .map((w) => (w.id === saved.id ? saved : w)),
    });
  }

  canEditGlobal() {
    return true;
  }

  async resetDashboard() {
    await this.api.removeDashboard(this._state.dashboard().id);
  }

  recomputeAfterSqueeze(
    widgets: Widget[],
    squeezedTypes: WidgetType[],
    gridWidth = 4,
    columnWidth = 2,
  ): Widget[] {
    if (!squeezedTypes?.length) {
      return widgets;
    }

    const out = widgets.map((w) => ({ ...w })); // pure

    // Squeeze : on ne touche qu'à h/color/squeeze. La largeur (w) est laissée
    // intacte — sinon les widgets full-width (ex: Planning avec w=4) sont
    // écrasés à columnWidth dès qu'un autre widget est squeezé.
    const squeezedSet = new Set(squeezedTypes);
    for (const w of out) {
      const isSqueezed = squeezedSet.has(w.type);
      w.h = isSqueezed ? 1 : w.h;
      w.color = isSqueezed ? 8 : w.color;
      w.squeeze = isSqueezed;
    }

    // Compactage Y avec respect du span horizontal de chaque widget.
    // Un widget de largeur columnWidth occupe 1 colonne, un widget full-width
    // (gridWidth) occupe toutes les colonnes et bloque la ligne entière.
    const cols = Math.max(1, Math.floor(gridWidth / columnWidth));
    const colCursors = new Array(cols).fill(0);

    // Tri stable par y, puis x — préserve l'ordre relatif voulu par l'utilisateur.
    out.sort((a, b) => a.y - b.y || a.x - b.x);

    for (const w of out) {
      const span = Math.max(1, Math.ceil((w.w ?? columnWidth) / columnWidth));
      let col = Math.floor((w.x ?? 0) / columnWidth);
      if (!Number.isFinite(col)) col = 0;
      col = Math.min(Math.max(0, col), cols - span);

      // y = plus haut cursor parmi les colonnes occupées par ce widget
      let y = 0;
      for (let i = 0; i < span; i++) {
        y = Math.max(y, colCursors[col + i]);
      }
      w.x = col * columnWidth;
      w.y = y;

      // Avance les cursors des colonnes occupées
      for (let i = 0; i < span; i++) {
        colCursors[col + i] = y + w.h;
      }
    }

    out.sort((a, b) => a.y - b.y || a.x - b.x);
    return out;
  }
}
